import * as FluxUtils from 'flux/utils';

import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { GlobalHelper } from '../utils/GlobalHelper';
import { ActionConstants } from '../constants/ActionConstants';

// Submodules
import { ArState, ArCore } from '../submodules/ar-manager/ArCore';
import { VisibilityActions } from '../actions/VisibilityActions';
import { ViewSettingsActions } from '../actions/ViewSettingsActions';

import core = egr.wcf.core;
import pl = egr.wcf.pl;

export interface ArStoreState extends ArState {
    arDevice: boolean;
}

class BoxArCore extends ArCore {
    protected groundSettingsOnEnter: { floorPlan: number, floorPlane: number } = { floorPlan: 1, floorPlane: 1 };
    protected automaticInteractorVisibilityBackup: boolean;
    protected rotateXEnabledBackup: boolean;
    protected rotateZEnabledBackup: boolean;

    constructor(protected coreApp: core.Application) {
        super(coreApp);
        this.onEnterAr = this.onEnterAr.bind(this);
        this.onLeaveAr = this.onLeaveAr.bind(this);
    }

    protected async onEnterAr(): Promise<void> {
        document.body.classList.add('ar-enabled'); // make body transparent
        if (GlobalHelper.floorPlan != null && GlobalHelper.floorPlan.visibility !== 0) {
            this.groundSettingsOnEnter.floorPlan = GlobalHelper.floorPlan.visibility;
            GlobalHelper.floorPlan.visibility = 0;
        }
        this.groundSettingsOnEnter.floorPlane = GlobalHelper.floorPlane.visibility;
        GlobalHelper.floorPlane.visibility = 0;
        GlobalHelper.lineMesh.setEnabled(false);
        VisibilityActions.setFullscreen(true);
        ViewSettingsActions.triggerResize();
    }

    protected async onLeaveAr(): Promise<void> {
        document.body.classList.remove('ar-enabled');
        if (GlobalHelper.floorPlan != null && GlobalHelper.floorPlan.visibility !== this.groundSettingsOnEnter.floorPlan) {
            GlobalHelper.floorPlan.visibility = this.groundSettingsOnEnter.floorPlan;
        }
        GlobalHelper.floorPlane.visibility = this.groundSettingsOnEnter.floorPlane;
        GlobalHelper.lineMesh.setEnabled(true);
        VisibilityActions.setFullscreen(false);
        ViewSettingsActions.triggerResize();
    }

    protected startTool(): void {
        if (this.coreApp.tools.defaultTool instanceof pl.tool.Default) {
            this.automaticInteractorVisibilityBackup = this.coreApp.tools.defaultTool.automaticInteractorVisibility;
            this.rotateXEnabledBackup = this.coreApp.tools.defaultTool.rotateXEnabled;
            this.rotateZEnabledBackup = this.coreApp.tools.defaultTool.rotateZEnabled;

            this.coreApp.tools.defaultTool.automaticInteractorVisibility = false;
            this.coreApp.tools.defaultTool.rotateXEnabled = false;
            this.coreApp.tools.defaultTool.rotateZEnabled = false;
        }
        this.coreApp.tools.stopActiveTool(); // force default tool restart
    }

    protected restoreTool(): void {
        if (this.coreApp.tools.defaultTool instanceof pl.tool.Default) {
            this.coreApp.tools.defaultTool.automaticInteractorVisibility = this.automaticInteractorVisibilityBackup;
            this.coreApp.tools.defaultTool.rotateXEnabled = this.rotateXEnabledBackup;
            this.coreApp.tools.defaultTool.rotateZEnabled = this.rotateZEnabledBackup;
        }
        this.coreApp.tools.stopActiveTool(); // force default tool restart
    }
}

class ArStoreClass extends FluxUtils.ReduceStore<ArStoreState, DispatcherBaseAction> {
    public get state(): ArState {
        return this.getState();
    }

    protected arCore: ArCore | undefined;

    public get core(): ArCore {
        if (this.arCore == null) {
            throw new Error('ArManager not initialized');
        }
        return this.arCore;
    }

    public initialize(): void {
        this.arCore = new BoxArCore(GlobalHelper.coreApp);
        this.arCore.stateEvent.addListener(this.update);
        this.update(this.arCore.getState());
    }

    public getInitialState(): ArStoreState {
        return {
            cameraIsFrozen: false,
            objectIsPlaced: false,
            started: false,
            arDevice: false
        };
    }

    public reduce(state: ArStoreState, action: DispatcherBaseAction): ArStoreState {
        // Note: this function should be `pure` -> we have to return a copy
        // of the current state if some property was changed

        switch (action.actionType) {
            case ActionConstants.AR_DEVICE_DETECTED:
                return jQuery.extend({}, state, {arDevice: true});
            case ActionConstants.AR_UPDATE:
                return jQuery.extend({}, state, action.value);
            default:
                break;
        }

        return state;
    }

    protected enable(): void {
        /* */
    }

    protected update(value: ArState | undefined): void {
        if (value == null) {
            return;
        }

        AppDispatcher.dispatch({
            actionType: ActionConstants.AR_UPDATE,
            value
        });
    }
}

export let ArStore: ArStoreClass = new ArStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/ArStore.ts